import axios from 'axios';
import { tokenManager } from './tokenManager';

// Empty string would short-circuit the URL composition below into an invalid
// request. Fall back to the canonical demo gateway in production builds and
// to the local dev gateway otherwise. VITE_JMAP_GATEWAY_URL still wins when
// supplied.
const __JMAP_PROD = (import.meta as any).env?.PROD;
const JMAP_GATEWAY_URL = (import.meta as any).env?.VITE_JMAP_GATEWAY_URL
  || (__JMAP_PROD ? 'https://demo.mail.melpapp.ai/gateway' : 'http://localhost:8082');

// Track requests waiting for token refresh
interface PendingRequest {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

let isRefreshing = false;
let pendingRequests: PendingRequest[] = [];

function processPendingRequests(token: string | null, error?: Error): void {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error || new Error('Token refresh failed'));
    } else {
      resolve(token);
    }
  });
  pendingRequests = [];
}

export class JmapClient {
  private sessionUrl: string;
  private apiUrl: string;

  constructor(_token?: string) {
    // Token parameter kept for backward compatibility but not used
    // Token is now fetched dynamically from tokenManager
    this.sessionUrl = `${JMAP_GATEWAY_URL}/jmap/session`;
    this.apiUrl = `${JMAP_GATEWAY_URL}/jmap/api`;
  }

  private getHeaders() {
    const token = tokenManager.getToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  private getToken(): string {
    return tokenManager.getToken() || '';
  }

  /**
   * Execute request with automatic 401 handling and retry
   */
  private async executeWithRetry<T>(requestFn: () => Promise<T>): Promise<T> {
    try {
      return await requestFn();
    } catch (error: any) {
      // Check for 401 error
      if (error.response?.status === 401) {
        // If already refreshing, wait for that to complete
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            pendingRequests.push({ resolve, reject });
          }).then(() => requestFn());
        }

        isRefreshing = true;

        try {
          const newToken = await tokenManager.handleUnauthorized();

          if (newToken) {
            processPendingRequests(newToken);
            // Retry the original request
            return await requestFn();
          } else {
            const authError = new Error('Session expired. Please login again.');
            processPendingRequests(null, authError);
            throw authError;
          }
        } catch (refreshError) {
          const authError = refreshError instanceof Error ? refreshError : new Error('Token refresh failed');
          processPendingRequests(null, authError);
          throw authError;
        } finally {
          isRefreshing = false;
        }
      }

      throw error;
    }
  }

  private normalizeMessageIds(value?: string | string[]) {
    if (!value) return undefined;
    const ids = Array.isArray(value) ? value : [value];
    const cleaned = ids.map((id) => id?.trim()).filter((id) => id);
    return cleaned.length ? cleaned : undefined;
  }

  async getSession() {
    return this.executeWithRetry(async () => {
      const response = await axios.get(this.sessionUrl, {
        headers: this.getHeaders()
      });
      return response.data;
    });
  }

  async request(methodCalls: any[]) {
    return this.executeWithRetry(async () => {
      // Determine required capabilities based on method calls
      const using = ['urn:ietf:params:jmap:core', 'urn:ietf:params:jmap:mail'];
      if (methodCalls.some(mc => typeof mc?.[0] === 'string' && mc[0].startsWith('EmailSubmission/'))) {
        using.push('urn:ietf:params:jmap:submission');
      }
      const response = await axios.post(this.apiUrl, {
        using,
        methodCalls
      }, {
        headers: this.getHeaders()
      });
      // Defensive guard: the gateway sometimes returns a non-JMAP body
      // (HTML error page, auth-renewal stall, intermediary timeout) with
      // a 200 status. Without this, every call site crashes with the
      // cryptic "Cannot read properties of undefined (reading '0')"
      // when it accesses `.methodResponses[0][1].ids`. Surface a clean
      // error so the call site can show a toast / retry instead.
      const body = response.data;
      if (!body || !Array.isArray(body.methodResponses)) {
        throw new Error('Invalid JMAP response: missing methodResponses');
      }
      return body;
    });
  }

  async getMailboxes(accountId: string) {
    const response = await this.request([
      ['Mailbox/get', {
        accountId,
        ids: null
      }, '0']
    ]);
    return response.methodResponses[0][1].list;
  }

  async getEmails(accountId: string, mailboxId: string, limit = 50) {
    // First, query for email IDs
    const queryResponse = await this.request([
      ['Email/query', {
        accountId,
        filter: { inMailbox: mailboxId },
        sort: [{ property: 'receivedAt', isAscending: false }],
        limit
      }, '0']
    ]);

    const emailIds = queryResponse.methodResponses[0][1].ids;

    if (emailIds.length === 0) {
      return [];
    }

    // Then get email details
    const getResponse = await this.request([
      ['Email/get', {
        accountId,
        ids: emailIds,
        // Include keywords so flags/stars persist after reloads
        properties: ['id', 'threadId', 'messageId', 'inReplyTo', 'references', 'mailboxIds', 'from', 'to', 'subject', 'receivedAt', 'sentAt', 'size', 'preview', 'hasAttachment', 'keywords']
      }, '1']
    ]);

    return getResponse.methodResponses[0][1].list;
  }

  /**
   * Sign a batch of remote image URLs through the gateway's image-proxy
   * endpoint. Returns a map from original URL → absolute proxy URL that the
   * webmail can drop into <img src=...>. Any URL the gateway refuses to
   * sign (private host, bad scheme, missing config) is omitted from the
   * result; the caller falls back to leaving the image blocked.
   */
  async signImageProxyUrls(urls: string[]): Promise<Map<string, string>> {
    if (!urls || urls.length === 0) return new Map();
    const unique = Array.from(new Set(urls.filter((u) => /^https?:\/\//i.test(u))));
    if (unique.length === 0) return new Map();
    try {
      const res = await fetch(`${JMAP_GATEWAY_URL}/img-proxy/sign`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ urls: unique }),
      });
      if (!res.ok) return new Map();
      const data: { proxyPaths?: Record<string, string> } = await res.json();
      const out = new Map<string, string>();
      const paths = data.proxyPaths || {};
      for (const [orig, path] of Object.entries(paths)) {
        if (typeof path === 'string' && path.startsWith('/')) {
          out.set(orig, `${JMAP_GATEWAY_URL}${path}`);
        }
      }
      return out;
    } catch {
      return new Map();
    }
  }

  /**
   * Delta-fetch: return only emails in the mailbox whose receivedAt is >= afterIso.
   * Used to merge newly-arrived mail into an already-loaded list without refetching the whole list.
   */
  async getEmailsAfter(accountId: string, mailboxId: string, afterIso: string, limit = 30) {
    const queryResponse = await this.request([
      ['Email/query', {
        accountId,
        filter: { conditions: [{ inMailbox: mailboxId }, { after: afterIso }], operator: 'AND' },
        sort: [{ property: 'receivedAt', isAscending: false }],
        limit,
      }, '0'],
    ]);
    const emailIds = queryResponse.methodResponses[0][1].ids;
    if (!emailIds || emailIds.length === 0) return [];

    const getResponse = await this.request([
      ['Email/get', {
        accountId,
        ids: emailIds,
        properties: ['id', 'threadId', 'messageId', 'inReplyTo', 'references', 'mailboxIds', 'from', 'to', 'subject', 'receivedAt', 'sentAt', 'size', 'preview', 'hasAttachment', 'keywords'],
      }, '1'],
    ]);
    return getResponse.methodResponses[0][1].list;
  }

  async searchEmails(accountId: string, mailboxId: string, searchQuery: string, filters: {
    from?: string;
    to?: string;
    cc?: string;
    subject?: string;
    hasAttachment?: boolean;
    after?: string; // yyyy-mm-dd
    before?: string; // yyyy-mm-dd
  } = {}, limit = 50) {
    // Build JMAP filter
    const jmapFilters: any[] = [{ inMailbox: mailboxId }];

    // Add search query filter (searches in subject, from, to, body)
    if (searchQuery) {
      jmapFilters.push({ text: searchQuery });
    }

    // Add from filter
    if (filters.from) {
      jmapFilters.push({ from: filters.from });
    }

    // Add to filter
    if (filters.to) {
      jmapFilters.push({ to: filters.to });
    }

    // Add subject filter
    if (filters.subject) {
      jmapFilters.push({ subject: filters.subject });
    }

    // Add attachment filter
    if (filters.hasAttachment) {
      jmapFilters.push({ hasAttachment: true });
    }

    if (filters.cc) {
      jmapFilters.push({ cc: filters.cc });
    }

    if (filters.after) {
      const after = new Date(filters.after).toISOString();
      jmapFilters.push({ after });
    }
    if (filters.before) {
      const before = new Date(filters.before).toISOString();
      jmapFilters.push({ before });
    }

    // Combine filters with AND operator
    const filter = jmapFilters.length > 1 ? { operator: 'AND', conditions: jmapFilters } : jmapFilters[0];

    // Query for email IDs
    const queryResponse = await this.request([
      ['Email/query', {
        accountId,
        filter,
        sort: [{ property: 'receivedAt', isAscending: false }],
        limit
      }, '0']
    ]);

    const emailIds = queryResponse.methodResponses[0][1].ids;

    if (emailIds.length === 0) {
      return [];
    }

    // Get email details
    const getResponse = await this.request([
      ['Email/get', {
        accountId,
        ids: emailIds,
        properties: ['id', 'threadId', 'messageId', 'inReplyTo', 'references', 'mailboxIds', 'from', 'to', 'subject', 'receivedAt', 'size', 'preview', 'hasAttachment', 'keywords']
      }, '1']
    ]);

    return getResponse.methodResponses[0][1].list;
  }

  async getEmail(accountId: string, emailId: string) {
    const response = await this.request([
      ['Email/get', {
        accountId,
        ids: [emailId],
        properties: [
          'id',
          'subject',
          'from',
          'to',
          'cc',
          'bcc',
          'keywords',
          'mailboxIds',
          'messageId',
          'inReplyTo',
          'references',
          'attachments',
          'textBody',
          'htmlBody',
          'bodyValues',
        ],
        bodyProperties: ['partId', 'type', 'size', 'name', 'cid', 'charset'],
        fetchAllBodyValues: true,
        maxBodyValueBytes: 524288
      }, '0']
    ]);
    const getResponse = Array.isArray(response.methodResponses)
      ? response.methodResponses.find((mr: any[]) => mr?.[0] === 'Email/get')
      : null;
    const list = getResponse?.[1]?.list;
    if (!Array.isArray(list) || list.length === 0) {
      throw new Error('Email not found');
    }
    return list[0];
  }

  /**
   * Fetch all emails belonging to a thread using Thread/get then Email/get
   */
  async getThreadEmails(accountId: string, threadId: string) {
    const threadResp = await this.request([
      ['Thread/get', { accountId, ids: [threadId] }, '0']
    ]);
    const list = threadResp?.methodResponses?.[0]?.[1]?.list || [];
    const emailIds = Array.isArray(list) && list.length ? (list[0].emailIds || []) : [];
    if (!emailIds.length) return [];
    const getResponse = await this.request([
      ['Email/get', {
        accountId,
        ids: emailIds,
        properties: ['id', 'threadId', 'messageId', 'inReplyTo', 'references', 'mailboxIds', 'from', 'to', 'subject', 'receivedAt', 'size', 'preview', 'hasAttachment', 'keywords']
      }, '1']
    ]);
    return getResponse.methodResponses[0][1].list || [];
  }

  async getThreadCounts(accountId: string, threadIds: string[]) {
    if (!threadIds.length) return {};
    const response = await this.request([
      ['Thread/get', { accountId, ids: threadIds }, '0']
    ]);
    const list = response?.methodResponses?.[0]?.[1]?.list || [];
    const counts: Record<string, number> = {};
    list.forEach((thread: any) => {
      if (!thread?.id) return;
      counts[thread.id] = Array.isArray(thread.emailIds) ? thread.emailIds.length : 0;
    });
    console.log(list,'<<<<');

    return counts;
  }

  async composeAndSendEmail(accountId: string, draft: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    bodyHtml?: string;
    fromEmail?: string;
    fromName?: string;
    replyTo?: string;
    inReplyTo?: string | string[];
    references?: string | string[];
    scheduledAt?: string;
    attachments?: Array<{ blobId: string; type: string; name?: string; size?: number; inline?: boolean; cid?: string }>
  }) {
    // Resolve Outbox and Sent mailbox ids and sender display name
    const mboxes = await this.getMailboxes(accountId);
    const outbox = mboxes.find((m: any) => m.role === 'outbox');
    const sent = mboxes.find((m: any) => m.role === 'sent')
      || mboxes.find((m: any) => (m.name || '').toLowerCase() === 'sent')
      || mboxes.find((m: any) => (m.name || '').toLowerCase().includes('sent'));
    if (!outbox?.id) {
      throw new Error('Outbox mailbox not found');
    }

    // Resolve sender identity; prefer provided draft.from, otherwise use account config
    let senderEmail = draft.fromEmail;
    let senderName: string | undefined = draft.fromName;
    if (!senderEmail) {
      try {
        const cfg = await this.getAccountConfig();
        senderEmail = cfg.email;
        senderName = cfg.displayName || undefined;
      } catch {
        // ignore, will fallback to first recipient domain
      }
    }

    const emailPayload = {
      mailboxIds: { [sent.id]: true },
      keywords: { '$seen': true },
      from: senderEmail ? [{ email: senderEmail, ...(senderName ? { name: senderName } : {}) }] : undefined,
      to: draft.to.map(email => ({ email })),
      cc: draft.cc?.map(email => ({ email })),
      bcc: draft.bcc?.map(email => ({ email })),
      replyTo: draft.replyTo ? [{ email: draft.replyTo }] : undefined,
      subject: draft.subject,
      bodyValues: (draft.bodyHtml ? {
        body: { value: draft.body, isTruncated: false },
        html: { value: draft.bodyHtml, isTruncated: false }
      } : {
        body: { value: draft.body, isTruncated: false }
      }),
      textBody: [{ partId: 'body', type: 'text/plain' }],
      ...(draft.bodyHtml ? { htmlBody: [{ partId: 'html', type: 'text/html' }] } : {}),
      ...(draft.attachments && draft.attachments.length ? {
        attachments: draft.attachments.map(a => ({
          blobId: a.blobId,
          type: a.type,
          name: a.name,
          size: a.size,
          disposition: a.inline ? 'inline' : 'attachment',
          ...(a.cid ? { cid: a.cid } : {})
        }))
      } : {}),
      // Do not set $draft when sending; server will move to Sent on submission
      ...(draft.inReplyTo ? { inReplyTo: this.normalizeMessageIds(draft.inReplyTo) } : {}),
      ...(draft.references ? { references: this.normalizeMessageIds(draft.references) } : {})
    };

    const sendResponse = await this.request([
      ['Email/set', {
        accountId,
        create: {
          draft1: emailPayload
        }
      }, '0'],
      ['EmailSubmission/set', {
        accountId,
        create: {
          submission1: {
            emailId: '#draft1',
            envelope: {
              mailFrom: { email: senderEmail || draft.to[0] },
              rcptTo: [
                ...draft.to.map(email => ({ email })),
                ...(draft.cc || []).map(email => ({ email })),
                ...(draft.bcc || []).map(email => ({ email }))
              ]
            },
            ...(draft.scheduledAt ? { sendAt: draft.scheduledAt } : {})
          }
        }
      }, '1']
    ]);

    const createdEmail = sendResponse.methodResponses[0][1]?.created?.draft1;
    if (!createdEmail) {
      throw new Error('Failed to create email draft');
    }

    return {
      email: createdEmail,
      submission: sendResponse.methodResponses[1][1]?.created?.submission1
    };
  }

  async sendEmail(accountId: string, email: any) {
    const response = await this.request([
      ['EmailSubmission/set', {
        accountId,
        create: {
          k1: {
            emailId: email.id,
            envelope: {
              mailFrom: email.from,
              rcptTo: email.to
            }
          }
        }
      }, '0']
    ]);
    return response.methodResponses[0][1];
  }

  async uploadBlob(accountId: string, file: File, onProgress?: (pct: number) => void) {
    return this.executeWithRetry(async () => {
      const response = await axios.post(
        `${JMAP_GATEWAY_URL}/jmap/upload/${accountId}`,
        await file.arrayBuffer(),
        {
          headers: {
            'Authorization': `Bearer ${this.getToken()}`,
            'Content-Type': file.type
          },
          onUploadProgress: (e: { loaded: number; total?: number }) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          }
        }
      );
      return response.data;
    });
  }

  /**
   * Get download URL for a blob (for inline images, etc.)
   * Note: This URL may expire if token expires. For downloads, use downloadBlobAsFile()
   */
  downloadBlob(accountId: string, blobId: string, name: string) {
    return `${JMAP_GATEWAY_URL}/jmap/download/${accountId}/${blobId}/${name}?token=${this.getToken()}`;
  }

  /**
   * Download a blob and trigger a file download in the browser
   * This uses fetch with proper auth handling to avoid browser auth dialogs
   */
  async downloadBlobAsFile(accountId: string, blobId: string, name: string): Promise<void> {
    return this.executeWithRetry(async () => {
      const url = `${JMAP_GATEWAY_URL}/jmap/download/${accountId}/${blobId}/${encodeURIComponent(name)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const error = new Error('Unauthorized') as any;
          error.response = { status: 401 };
          throw error;
        }
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL after a short delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    });
  }

  /**
   * Fetch blob data as a blob URL (for displaying inline images, etc.)
   * Returns a blob URL that can be used in img src, etc.
   */
  async fetchBlobAsUrl(accountId: string, blobId: string, name: string): Promise<string> {
    return this.executeWithRetry(async () => {
      const url = `${JMAP_GATEWAY_URL}/jmap/download/${accountId}/${blobId}/${encodeURIComponent(name)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const error = new Error('Unauthorized') as any;
          error.response = { status: 401 };
          throw error;
        }
        throw new Error(`Fetch failed: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    });
  }

  async deleteEmail(accountId: string, emailId: string) {
    const response = await this.request([
      ['Email/set', {
        accountId,
        destroy: [emailId]
      }, '0']
    ]);
    return response.methodResponses[0][1];
  }

  /**
   * Bulk-delete N emails in a single JMAP `Email/set` call. Use this in
   * place of looping `deleteEmail` — 50 emails go from 50 round-trips
   * to one.
   */
  async deleteEmails(accountId: string, emailIds: string[]) {
    if (emailIds.length === 0) return null;
    const response = await this.request([
      ['Email/set', {
        accountId,
        destroy: emailIds,
      }, '0'],
    ]);
    return response.methodResponses[0][1];
  }

  /**
   * Bulk-archive N emails into a single mailbox in one round-trip.
   * Builds an `update` map with the same `mailboxIds` payload per id.
   */
  async archiveEmails(accountId: string, emailIds: string[], archiveMailboxId: string) {
    if (emailIds.length === 0) return null;
    const update: Record<string, { mailboxIds: Record<string, boolean> }> = {};
    const newMailboxIds = { [archiveMailboxId]: true };
    for (const id of emailIds) {
      update[id] = { mailboxIds: newMailboxIds };
    }
    const response = await this.request([
      ['Email/set', { accountId, update }, '0'],
    ]);
    return response.methodResponses[0][1];
  }

  /**
   * Bulk keyword update — used for "mark as read", "mark as starred", etc.
   */
  async setKeywordBulk(accountId: string, emailIds: string[], keyword: string, value: boolean) {
    if (emailIds.length === 0) return null;
    const update: Record<string, Record<string, boolean | null>> = {};
    for (const id of emailIds) {
      update[id] = { [`keywords/${keyword}`]: value ? true : null };
    }
    const response = await this.request([
      ['Email/set', { accountId, update }, '0'],
    ]);
    return response.methodResponses[0][1];
  }

  async toggleFlag(accountId: string, emailId: string, isFlagged: boolean) {
    const response = await this.request([
      ['Email/set', {
        accountId,
        update: {
          [emailId]: {
            // Patch-style update: set or unset only $flagged keyword
            'keywords/$flagged': isFlagged ? true : null
          }
        }
      }, '0']
    ]);
    return response.methodResponses[0][1];
  }

  async setKeyword(accountId: string, emailId: string, keyword: string, value: boolean) {
    const response = await this.request([
      ['Email/set', {
        accountId,
        update: {
          [emailId]: {
            [`keywords/${keyword}`]: value ? true : null
          }
        }
      }, '0']
    ]);
    return response.methodResponses[0][1];
  }

  async markAsRead(accountId: string, emailId: string, isRead: boolean) {
    const response = await this.request([
      ['Email/set', {
        accountId,
        update: {
          [emailId]: {
            // Patch-style update: set or unset only $seen keyword
            'keywords/$seen': isRead ? true : null
          }
        }
      }, '0']
    ]);
    return response.methodResponses[0][1];
  }

  async archiveEmail(accountId: string, emailId: string, archiveMailboxId: string, _currentMailboxIds: { [key: string]: boolean }) {
    // Remove from current mailboxes and add to archive
    const newMailboxIds = { [archiveMailboxId]: true };

    const response = await this.request([
      ['Email/set', {
        accountId,
        update: {
          [emailId]: {
            mailboxIds: newMailboxIds
          }
        }
      }, '0']
    ]);
    return response.methodResponses[0][1];
  }

  async getDomainContacts() {
    return this.executeWithRetry(async () => {
      const response = await axios.get(`${JMAP_GATEWAY_URL}/user/contacts`, {
        headers: this.getHeaders()
      });
      return response.data;
    });
  }

  async getAccountConfig() {
    return this.executeWithRetry(async () => {
      const response = await axios.get(`${JMAP_GATEWAY_URL}/user/account-config`, {
        headers: this.getHeaders()
      });
      return response.data;
    });
  }

  async updateDisplayName(displayName: string) {
    return this.executeWithRetry(async () => {
      const response = await axios.patch(`${JMAP_GATEWAY_URL}/user/profile`, { displayName }, {
        headers: this.getHeaders()
      });
      return response.data as { displayName: string };
    });
  }

  /**
   * Create a new mailbox/folder
   */
  async createMailbox(accountId: string, name: string, parentId?: string) {
    const response = await this.request([
      ['Mailbox/set', {
        accountId,
        create: {
          newMailbox: {
            name,
            ...(parentId ? { parentId } : {})
          }
        }
      }, '0']
    ]);

    const result = response.methodResponses[0][1];
    if (result.created?.newMailbox) {
      return result.created.newMailbox;
    }
    throw new Error(result.notCreated?.newMailbox?.description || 'Failed to create mailbox');
  }

  /**
   * Rename a mailbox/folder
   */
  async renameMailbox(accountId: string, mailboxId: string, newName: string) {
    const response = await this.request([
      ['Mailbox/set', {
        accountId,
        update: {
          [mailboxId]: {
            name: newName
          }
        }
      }, '0']
    ]);

    const result = response.methodResponses[0][1];
    if (result.updated && result.updated[mailboxId]) {
      return result.updated[mailboxId];
    }
    throw new Error(result.notUpdated?.[mailboxId]?.description || 'Failed to rename mailbox');
  }

  /**
   * Delete a mailbox/folder
   */
  async deleteMailbox(accountId: string, mailboxId: string) {
    const response = await this.request([
      ['Mailbox/set', {
        accountId,
        destroy: [mailboxId]
      }, '0']
    ]);

    const result = response.methodResponses[0][1];
    if (result.destroyed && result.destroyed.includes(mailboxId)) {
      return true;
    }
    throw new Error(result.notDestroyed?.[mailboxId]?.description || 'Failed to delete mailbox');
  }

  /**
   * Move email to a different mailbox
   */
  async moveEmail(accountId: string, emailId: string, targetMailboxId: string, _currentMailboxIds: { [key: string]: boolean }) {
    const newMailboxIds = { [targetMailboxId]: true };

    const response = await this.request([
      ['Email/set', {
        accountId,
        update: {
          [emailId]: {
            mailboxIds: newMailboxIds
          }
        }
      }, '0']
    ]);

    const result = response.methodResponses[0][1];
    if (result.updated && Object.prototype.hasOwnProperty.call(result.updated, emailId)) {
      return result.updated[emailId];
    }
    throw new Error(result.notUpdated?.[emailId]?.description || 'Failed to move email');
  }

  /**
   * Move many emails into a single mailbox in ONE JMAP request. The
   * previous bulk-move code looped per-id and awaited each `moveEmail`
   * sequentially — 50 selected emails = 50 round-trips. JMAP's Email/set
   * accepts a multi-id update map, so we collapse to a single call.
   *
   * Returns the list of ids the server reported updated. Ids that
   * appeared in `notUpdated` are reported via the `errors` array so the
   * caller can show a partial-success message.
   */
  async moveEmails(accountId: string, emailIds: string[], targetMailboxId: string): Promise<{ updated: string[]; errors: Array<{ id: string; reason: string }> }> {
    if (emailIds.length === 0) return { updated: [], errors: [] };
    const newMailboxIds = { [targetMailboxId]: true };
    const update: Record<string, { mailboxIds: { [k: string]: boolean } }> = {};
    for (const id of emailIds) {
      update[id] = { mailboxIds: newMailboxIds };
    }
    const response = await this.request([
      ['Email/set', { accountId, update }, '0'],
    ]);
    const result = response.methodResponses[0][1];
    const updated = result.updated ? Object.keys(result.updated) : [];
    const errors: Array<{ id: string; reason: string }> = [];
    if (result.notUpdated) {
      for (const id of Object.keys(result.notUpdated)) {
        errors.push({ id, reason: result.notUpdated[id]?.description || 'unknown' });
      }
    }
    return { updated, errors };
  }

  /**
   * Delete all emails inside a mailbox
   */
  async emptyMailbox(accountId: string, mailboxId: string) {
    const limit = 100;
    while (true) {
      const queryResponse = await this.request([
        ['Email/query', {
          accountId,
          filter: { inMailbox: mailboxId },
          limit
        }, '0']
      ]);
      const ids = queryResponse.methodResponses?.[0]?.[1]?.ids || [];
      if (!ids.length) break;
      await this.request([
        ['Email/set', {
          accountId,
          destroy: ids
        }, '1']
      ]);
    }
    return true;
  }

  /**
   * Mark all emails inside a mailbox as read
   */
  async markAllAsRead(accountId: string, mailboxId: string) {
    const limit = 100;
    while (true) {
      const queryResponse = await this.request([
        ['Email/query', {
          accountId,
          filter: {
            operator: 'AND',
            conditions: [
              { inMailbox: mailboxId },
              { notKeyword: '$seen' }
            ]
          },
          limit
        }, '0']
      ]);
      const ids = queryResponse.methodResponses?.[0]?.[1]?.ids || [];
      if (!ids.length) break;
      const update = ids.reduce((acc: Record<string, any>, id: string) => {
        acc[id] = { 'keywords/$seen': true };
        return acc;
      }, {});
      await this.request([
        ['Email/set', {
          accountId,
          update
        }, '1']
      ]);
    }
    return true;
  }

  /**
   * Create a draft email in the drafts mailbox
   */
  async createDraft(accountId: string, draft: {
    subject?: string;
    from?: Array<{ name?: string; email: string }>;
    to?: Array<{ name?: string; email: string }>;
    cc?: Array<{ name?: string; email: string }>;
    bcc?: Array<{ name?: string; email: string }>;
    replyTo?: Array<{ name?: string; email: string }>;
    textBody?: string;
    htmlBody?: string;
    inReplyTo?: string | string[];
    references?: string | string[];
    attachments?: Array<{ blobId: string; type: string; name?: string; size?: number; inline?: boolean; cid?: string }>;
    keywords?: { [key: string]: boolean | null };
  }) {
    // First get the drafts mailbox
    const mailboxesResponse = await this.request([
      ['Mailbox/get', {
        accountId,
        ids: null
      }, '0']
    ]);

    const mailboxes = mailboxesResponse.methodResponses[0][1].list;
    const draftsMailbox = mailboxes.find((m: any) => m.role === 'drafts');

    if (!draftsMailbox) {
      throw new Error('Drafts mailbox not found');
    }

    // Create email in drafts mailbox
    const emailData: any = {
      mailboxIds: { [draftsMailbox.id]: true },
      keywords: { '$draft': true },
    };

    if (draft.subject) emailData.subject = draft.subject;
    if (draft.from) emailData.from = draft.from;
    if (draft.to) emailData.to = draft.to;
    if (draft.cc) emailData.cc = draft.cc;
    if (draft.bcc) emailData.bcc = draft.bcc;
    if (draft.replyTo) emailData.replyTo = draft.replyTo;
    if (draft.inReplyTo) emailData.inReplyTo = this.normalizeMessageIds(draft.inReplyTo);
    if (draft.references) emailData.references = this.normalizeMessageIds(draft.references);
    emailData.keywords = draft.keywords || { '$draft': true };

    // Build body structure (only include parts that exist to avoid server validation errors)
    const hasText = !!(draft.textBody && draft.textBody.trim());
    const hasHtml = (() => {
      if (!draft.htmlBody) return false;
      const stripped = draft.htmlBody
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .trim();
      return stripped.length > 0;
    })();

    if (hasText || hasHtml) {
      emailData.bodyValues = {};

      if (hasText) {
        emailData.bodyValues['text'] = { value: draft.textBody };
        emailData.textBody = [{ partId: 'text', type: 'text/plain' }];
      }

      if (hasHtml) {
        emailData.bodyValues['html'] = { value: draft.htmlBody };
        emailData.htmlBody = [{ partId: 'html', type: 'text/html' }];
      }
    }

    if (draft.attachments && draft.attachments.length) {
      emailData.attachments = draft.attachments.map((a) => ({
        blobId: a.blobId,
        type: a.type,
        name: a.name,
        size: a.size,
        disposition: a.inline ? 'inline' : 'attachment',
        ...(a.cid ? { cid: a.cid } : {})
      }));
    }

    const response = await this.request([
      ['Email/set', {
        accountId,
        create: {
          draft: emailData
        }
      }, '0']
    ]);

    const result = response.methodResponses[0][1];
    if (result.created?.draft) {
      return result.created.draft;
    }
    throw new Error(result.notCreated?.draft?.description || 'Failed to create draft');
  }

  /**
   * Update an existing draft email
   */
  async updateDraft(accountId: string, emailId: string, draft: {
    subject?: string;
    to?: Array<{ name?: string; email: string }>;
    cc?: Array<{ name?: string; email: string }>;
    bcc?: Array<{ name?: string; email: string }>;
    textBody?: string;
    htmlBody?: string;
    inReplyTo?: string | string[];
    references?: string | string[];
    attachments?: Array<{ blobId: string; type: string; name?: string; size?: number; inline?: boolean; cid?: string }>;
    keywords?: { [key: string]: boolean | null };
  }) {
    const updateData: any = {};

    if (draft.subject !== undefined) updateData.subject = draft.subject;
    if (draft.to) updateData.to = draft.to;
    if (draft.cc) updateData.cc = draft.cc;
    if (draft.bcc) updateData.bcc = draft.bcc;
    if (draft.inReplyTo) updateData.inReplyTo = this.normalizeMessageIds(draft.inReplyTo);
    if (draft.references) updateData.references = this.normalizeMessageIds(draft.references);
    if (draft.keywords) updateData.keywords = draft.keywords;

    // Build body structure if body content is provided
    // Do not send bodyValues on update to avoid server validation errors; rely on content set at creation

    if (draft.attachments !== undefined) {
      updateData.attachments = draft.attachments.map((a) => ({
        blobId: a.blobId,
        type: a.type,
        name: a.name,
        size: a.size,
        disposition: a.inline ? 'inline' : 'attachment',
        ...(a.cid ? { cid: a.cid } : {})
      }));
    }

    // If nothing to update, short-circuit to avoid server errors
    if (Object.keys(updateData).length === 0) {
      return { id: emailId };
    }

    const response = await this.request([
      ['Email/set', {
        accountId,
        update: {
          [emailId]: updateData
        }
      }, '0']
    ]);

    const setResponse = Array.isArray(response.methodResponses)
      ? response.methodResponses.find((mr: any[]) => mr?.[0] === 'Email/set')
      : null;
    const result = setResponse?.[1] || response.methodResponses?.[0]?.[1];

    if (result?.updated && result.updated[emailId]) {
      return { id: emailId, ...result.updated[emailId] };
    }

    const err = result?.notUpdated?.[emailId];
    if (err?.type === 'notFound') {
      throw new Error('Draft not found');
    }
    throw new Error(err?.description || 'Failed to update draft');
  }

  /**
   * Get drafts from the drafts mailbox
   */
  async getDrafts(accountId: string, limit = 50) {
    // First get the drafts mailbox
    const mailboxesResponse = await this.request([
      ['Mailbox/get', {
        accountId,
        ids: null
      }, '0']
    ]);

    const mailboxes = mailboxesResponse.methodResponses[0][1].list;
    const draftsMailbox = mailboxes.find((m: any) => m.role === 'drafts');

    if (!draftsMailbox) {
      return [];
    }

    // Get emails from drafts mailbox
    return this.getEmails(accountId, draftsMailbox.id, limit);
  }
}
